import {
  type DocumentPreSignedURLInput,
  type DocumentPreSignedURLOutput,
  type DocumentUpload,
  type ErrorType,
  type UseMutateAsyncFunction,
  useUploadFile,
} from 'api';
import { useCallback } from 'react';

import { UploadDocumentProps } from '../../types';

/**
 * A hook to upload a file and create a document record.
 *
 * If you can think of a simpler way to handle these extra fields, feel free to
 * improve this.
 *
 * @template ExtraFields - Additional fields for API requests, like `slug`.
 * @param generatePreSignedUrl - Function to generate a pre-signed URL.
 * @param createDocument - Function to save the document record.
 * @returns A function to handle the upload and creation process.
 */
export const useUploadAndCreateDocument = <ExtraFields extends Record<string, string | number>>(
  generatePreSignedUrl: UseMutateAsyncFunction<
    DocumentPreSignedURLOutput,
    ErrorType<Error>,
    { data: DocumentPreSignedURLInput } & ExtraFields
  >,
  createDocument: UseMutateAsyncFunction<void, ErrorType<Error>, { data: DocumentUpload } & ExtraFields>
) => {
  const { mutateAsync: uploadFile } = useUploadFile();

  return useCallback(
    async (document: UploadDocumentProps, extraFields: ExtraFields) => {
      const { upload_url, file_path } = await generatePreSignedUrl({
        ...extraFields,
        data: { content_type: document.file.type },
      });

      await uploadFile({
        file: document.file,
        headers: { 'content-type': document.file.type },
        url: upload_url,
      });

      await createDocument({
        ...extraFields,
        data: { document_type: document.type, file_path },
      });
    },
    [generatePreSignedUrl, createDocument, uploadFile]
  );
};
